/**
 * iter294 — Nettoyage des plaques : suppression des espaces et fusion des
 * véhicules en double. On montre d'abord ce qui sera fait (essai à blanc),
 * puis on applique.
 */
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Car, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const PlaquesDoublonsCard = () => {
  const [rapport, setRapport] = useState(null);
  const [busy, setBusy] = useState(false);

  const lancer = async (dryRun) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/vehicules/fusionner-doublons?dry_run=${dryRun}`);
      setRapport(data);
      if (!dryRun) {
        toast.success(
          `${data.plaques_corrigees} plaque(s) et ${data.vins_corriges || 0} châssis nettoyé(s), ${data.vehicules_supprimes} doublon(s) fusionné(s)`,
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Opération impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4 sm:p-5" data-testid="plaques-doublons-card">
      <div className="flex items-center gap-2 mb-1">
        <Car className="w-4 h-4 text-[#52a675]" />
        <h3 className="text-base font-semibold text-gray-900">Plaques et véhicules en double</h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Supprime les espaces des plaques (« GE391 763 » devient « GE391763 ») et des n° de châssis,
        puis fusionne les véhicules d&apos;un même client qui portent la même plaque. Le kilométrage
        le plus récent est conservé et servira aux prochains OR. Dossiers, factures, devis,
        rendez-vous et photos sont rattachés au véhicule conservé.
      </p>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline" size="sm" disabled={busy}
          onClick={() => lancer(true)}
          data-testid="plaques-doublons-simuler"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
          Voir ce qui serait corrigé
        </Button>
        {rapport?.dry_run && (rapport.plaques_corrigees > 0 || rapport.doublons > 0 || rapport.vins_corriges > 0) && (
          <Button
            size="sm" disabled={busy}
            className="bg-[#52a675] hover:bg-[#3d8a5c]"
            onClick={() => {
              if (window.confirm(
                `Appliquer ?\n\n${rapport.plaques_corrigees} plaque(s) nettoyée(s)\n`
                + `${rapport.vins_corriges || 0} n° de châssis nettoyé(s)\n`
                + `${rapport.doublons} doublon(s) fusionné(s)\n\nCette action est définitive.`,
              )) lancer(false);
            }}
            data-testid="plaques-doublons-appliquer"
          >
            Appliquer
          </Button>
        )}
      </div>

      {rapport && (
        <div className="mt-3 text-xs text-gray-700 space-y-1" data-testid="plaques-doublons-rapport">
          <div className="font-semibold">
            {rapport.dry_run ? "Essai à blanc — rien n'a été modifié" : "Nettoyage effectué"}
          </div>
          <div>{rapport.vehicules_examines} véhicules examinés</div>
          <div>{rapport.plaques_corrigees} plaque(s) à nettoyer</div>
          <div>{rapport.vins_corriges || 0} n° de châssis à nettoyer</div>
          <div>
            {rapport.doublons} doublon(s){!rapport.dry_run && ` · ${rapport.references_deplacees} rattachement(s) déplacé(s)`}
          </div>
          {(rapport.detail || []).slice(0, 10).map((f) => (
            <div key={f.supprime} className="text-gray-500">
              {(f.plaques_dorigine || []).join(" + ")} → {f.plaque} · km conservé {f.km_conserve || "—"}
            </div>
          ))}
          {(rapport.plaques_chez_plusieurs_clients || []).length > 0 && (
            <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-amber-900">
              <div className="font-semibold flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                {rapport.plaques_chez_plusieurs_clients.length} plaque(s) présentes chez plusieurs clients
              </div>
              <div className="mt-0.5">
                Non fusionnées : il s&apos;agit peut-être d&apos;un véhicule revendu. À trancher
                cas par cas dans la carte « Plaques partagées entre deux clients » ci-dessous.
              </div>
              <div className="mt-1 font-mono text-[11px]">
                {rapport.plaques_chez_plusieurs_clients.slice(0, 12).join(", ")}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default PlaquesDoublonsCard;
